import React, { useRef, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { SET_USER_ADMIN_COMMENT } from "../../api/graphql/users";
import { Manager, managerName } from "../ManagerSelect";
import dialog from "../../styles/dialog.module.scss";
import styles from "./styles.module.scss";

export type AdminComment = {
  comment: string;
  updatedAt?: string | null;
  updatedBy?: Manager | null;
};

// Same limit as MAX_ADMIN_COMMENT_LENGTH on the backend.
const MAX_LENGTH = 2000;

// The Date scalar may arrive as an ISO string or as milliseconds.
const formatEdited = (value?: string | null) => {
  if (!value) return "";
  const date = Number(value) > 1e12 ? dayjs(Number(value)) : dayjs(value);
  return date.isValid() ? date.format("YYYY.MM.DD HH:mm") : "";
};

interface UserCommentProps {
  userId: number;
  /** How the dialog names the client: their email, else their name. */
  userLabel: string;
  value?: AdminComment | null;
}

/** The admins' note on a client: a Users cell that opens an editor. */
const UserComment: React.FC<UserCommentProps> = ({
  userId,
  userLabel,
  value,
}) => {
  // Written to the cache, so the row shows the new text after a refilter.
  const [save, { loading }] = useMutation(SET_USER_ADMIN_COMMENT, {
    fetchPolicy: "network-only",
  });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const textArea = useRef<TextAreaRef>(null);

  const current = value?.comment ?? "";
  const edited = [
    value?.updatedBy ? managerName(value.updatedBy) : "",
    formatEdited(value?.updatedAt),
  ]
    .filter(Boolean)
    .join(" · ");

  const openEditor = () => {
    setDraft(current);
    setOpen(true);
  };

  const close = () => {
    if (!loading) setOpen(false);
  };

  const handleSave = async () => {
    const text = draft.trim();
    if (text === current) {
      setOpen(false);
      return;
    }
    try {
      await save({ variables: { userId, comment: text || null } });
      setOpen(false);
    } catch (error: any) {
      message.error(error?.message || "The comment was not saved");
    }
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.cell} ${current ? "" : styles.empty}`}
        title={current || undefined}
        aria-label={
          current
            ? `Edit the comment on ${userLabel}`
            : `Add a comment on ${userLabel}`
        }
        onClick={openEditor}
      >
        <span className={styles.text}>{current || "Add comment"}</span>
      </button>

      <Modal
        open={open}
        title={<span className={dialog.title}>Comment on {userLabel}</span>}
        onCancel={close}
        afterOpenChange={(opened) =>
          opened && textArea.current?.focus({ cursor: "end" })
        }
        width={480}
        footer={[
          <div key="footer" className={dialog.footer}>
            <Button className={dialog.cancel} onClick={close}>
              Cancel
            </Button>
            <Button type="primary" loading={loading} onClick={handleSave}>
              Save
            </Button>
          </div>,
        ]}
      >
        <p className={styles.hint}>
          Only admins see it, the client does not. Leave it empty to remove it.
        </p>
        <Input.TextArea
          ref={textArea}
          aria-label="Comment"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave();
          }}
          placeholder="e.g. Pays by invoice, contact on Telegram"
          autoSize={{ minRows: 4, maxRows: 10 }}
          maxLength={MAX_LENGTH}
          showCount
        />
        {edited && <p className={styles.meta}>Last edited: {edited}</p>}
      </Modal>
    </>
  );
};

export default UserComment;
